import React, {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {diApi} from '@di-shared/api/di';
import {InvalidOrAbnormal} from '@di-shared/api/di/type';
import {emitEventLog} from '@di-shared/features/analytics';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {SestCategory, SestCode} from '@di-shared/features/diag/sest/const';
import VehicleSestItemKr from '@di-shared/features/home/kr/vehicle/VehicleSestItem.kr';
import VehicleSestLoadingKr from '@di-shared/features/home/kr/vehicle/VehicleSestLoading.kr';
import VehicleSestTooltipKr from '@di-shared/features/home/kr/vehicle/VehicleSestTooltip.kr';
import {getBadges} from '@di-shared/features/home/kr/vehicle/utils';
import {ROUTE_PATHS} from '@di-shared/routes';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {WebviewUtils} from '@shared/webviewUtils';
import {useSuspenseQueries} from '@tanstack/react-query';

export interface VehicleSestKrProps {
  vin: string;
  ccid: string;

  onError?(): void;
}

function VehicleSestKr({onError, ...props}: VehicleSestKrProps) {
  return (
    <ErrorBoundary
      fallback={<></>}
      onError={onError}
    >
      <Suspense fallback={<VehicleSestLoadingKr />}>
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, ccid}: VehicleSestKrProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const [{data: sestData}, {data: warningLightData}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApi.maintenancesSestController.getAggregatedSestList1), vin, ccid],
        queryFn: () =>
          diApi.maintenancesSestController.getAggregatedSestList1({
            vin,
            ccid,
          }),
      },
      {
        queryKey: [ccWebviewUtils.getCarWarningLightStatus],
        queryFn: async () => {
          try {
            return await ccWebviewUtils.getCarWarningLightStatus();
          } catch (error) {
            console.warn('Warning light data fetch failed:', error);
            return null;
          }
        },
        retry: 1,
      },
    ],
  });
  const engineBadges = getBadges(
    sestData.data.sests.filter(({categoryNo}) => categoryNo === SestCategory.ENGINE),
    [
      {
        sestCode: SestCode.ENGINE_OIL_AND_FILTER,
        isSDKWarningLight: warningLightData?.engineOil === InvalidOrAbnormal.Abnormal,
      },
    ]
  );
  const brakeBadges = getBadges(sestData.data.sests.filter(({categoryNo}) => categoryNo === SestCategory.BRAKE));
  const airConditionerBadges = getBadges(sestData.data.sests.filter(({categoryNo}) => categoryNo === SestCategory.AIR_CONDITIONER));

  function linkToMaintenanceAppointment() {
    WebviewUtils.openNewWebView({
      url: `${import.meta.env.VITE_CCI_DOMAIN}/maintenance/register/appointment?vin=${vin}`,
      isModal: true,
      isNavigationBar: true,
      isToolBar: false,
    });
  }

  return (
    <section className="section section_consumable">
      <div className="section_container_header">
        <h3 className="title_xsmall_b">{t('T10105')}</h3>
        <VehicleSestTooltipKr />
        <button
          type="button"
          className="btn_history_view"
          onClick={() => navigate(ROUTE_PATHS.DIAG_EXCHANGE_HISTORY.getFullPath(vin))}
        >
          <span className="ic ic_history_16px" />
          {t('T30140')}
        </button>
      </div>

      <div className="section_container">
        <ul className="consumable_list">
          <VehicleSestItemKr
            vin={vin}
            title={t('T30050')}
            badges={engineBadges}
            icon={<span className="ic ic_power_train_24px" />}
            to={ROUTE_PATHS.DIAG_SEST_$CATEGORY.getFullPath(vin, `${SestCategory.ENGINE}`)}
          />
          <VehicleSestItemKr
            vin={vin}
            title={t('T30051')}
            badges={brakeBadges}
            icon={<span className="ic ic_brake_oil_24px" />}
            to={ROUTE_PATHS.DIAG_SEST_$CATEGORY.getFullPath(vin, `${SestCategory.BRAKE}`)}
          />
          <VehicleSestItemKr
            vin={vin}
            title={t('T30052')}
            badges={airConditionerBadges}
            icon={<span className="ic ic_vent_on_24px" />}
            to={ROUTE_PATHS.DIAG_SEST_$CATEGORY.getFullPath(vin, `${SestCategory.AIR_CONDITIONER}`)}
          />
        </ul>

        <button
          className="btn btn_large btn_filled btn_full"
          type="button"
          onClick={() => {
            emitEventLog({
              event: 'maintenance_appointment_interaction',
              interaction_type: 'cta click',
              click_text: t('T30024'),
              click_location: 'body',
            });
            linkToMaintenanceAppointment();
          }}
        >
          {t('T30024')}
        </button>
      </div>
    </section>
  );
}

export default VehicleSestKr;
